import { FlexDirectionProperty } from 'csstype';
import React from 'react';
import { absolutePositionFill } from './LayoutStyles';
import { classes, cssClass, Style } from './StyleFunctions';


type PropsWithChildren<P> = P & { children?: React.ReactNode };

export type OtherDivProps = Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'className'>;

export const errorNameContainsSpaces = 'DivWithStyle component name may not contain spaces';

export function divWithStyle(
  nameForComponentAndCssClass: string,
  styles: Style | Style[],
  definedDivProps?: OtherDivProps
): React.ComponentType<OtherDivProps> {

  if (nameForComponentAndCssClass.indexOf(' ') > -1) {
    throw new Error(errorNameContainsSpaces);
  }

  if (!Array.isArray(styles)) {
    styles = [styles];
  }

  const className = cssClass(nameForComponentAndCssClass, ...styles);

  return class extends React.Component<OtherDivProps> {

    public static displayName: string = nameForComponentAndCssClass;

    public override render() {
      const divPropsWithClassName = { ...definedDivProps, ...this.props, className };
      return (
        <div {...divPropsWithClassName}>
          {this.props.children}
        </div>
      );
    }
  };
}

export function HorizontalFlexContainer(props: PropsWithChildren<FlexContainerProps>): JSX.Element {
  const classNames = classes(
    HorizontalContainerClassName,
    props.fillFlexParent && FillParentClassName,
    ...(props.additionalClassNames || []),
  );
  return <div className={classNames}>{props.children}</div>;
}

export function VerticalFlexContainer(props: PropsWithChildren<FlexContainerProps>): JSX.Element {
  const classNames = classes(
    VerticalContainerClassName,
    props.fillFlexParent && FillParentClassName,
    ...(props.additionalClassNames || []),
  );
  return <div className={classNames}>{props.children}</div>;
}

export interface FlexContainerProps {
  fillFlexParent?: boolean;
  additionalClassNames?: string[];
}

export const FillParentClassName = 'fill-flex-parent';

const flexContainerContainerClass = (name: string, direction: FlexDirectionProperty) => {
  return cssClass(name, {
    display: 'flex',
    flexDirection: direction,
    $nest: {
      [`&.${FillParentClassName}`]: {
        flex: 1,
      }
    }
  });
}

export const HorizontalContainerClassName = flexContainerContainerClass('horizontal-flex-container', 'row');
export const VerticalContainerClassName = flexContainerContainerClass('vertical-flex-container', 'column');

export function FlexItem(props: PropsWithChildren<FlexItemProps>): JSX.Element {
  const classNames = classes(FlexItemClassName, props.fillRemainingSpace && FillRemainingSpaceClassName);
  return <div className={classNames}>{props.children}</div>;
}

export interface FlexItemProps {
  fillRemainingSpace?: boolean;
}

export const FillRemainingSpaceClassName = 'fill-remaining-space';

export const FlexItemClassName = cssClass('flex-item', {
  $nest: {
    [`&.${FillRemainingSpaceClassName}`]: {
      flexGrow: 1,
    }
  }
});

const superimposedDivsContainerClass = cssClass('superimposedDivsContainer', {
  height: '100%',
  position: 'relative',
});

/**
 * Renders each child inside its own absolutely-positioned div, all superimposed directly on top of one another.
 * Positions itself to fill its container height.
 */
export function SuperimposedDivs(props: { children: React.ReactElement | null | (React.ReactElement | null)[] }): JSX.Element {

  const childItems = (props.children instanceof Array) ? props.children : [props.children];
  const filteredChildItems = childItems.filter(item => item !== null) as React.ReactElement[];

  return (
    <div className={superimposedDivsContainerClass}>
      {filteredChildItems.map(item => (
        <div className={absolutePositionFill} key={item.key || undefined}>
          {item}
        </div>
      ))}
    </div>
  );
}
